'use client';
import { faCalendar, faClock, faCreditCard, faEdit, faLocationDot, faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Modal, ModalClose, ModalContent, ModalDescription, ModalHeading } from '@pickleballinc/react-ui';
import CustomLink from '@pickleballinc/ui/components/ui/custom-link';
import clsx from 'clsx';
import moment from 'moment';
import { Url } from 'next/dist/shared/lib/router/router';
import { useEffect, useState } from 'react';

import { IsManagerDetails } from '@/legacy/utils/tournaments.types';
import { useSession } from '@/lib/SessionProvider';
import { useAuth } from '@/permissions/AuthProvider';
import { Permission } from '@/permissions/permission';
import { IntroCard } from '@/tournament/components/details/IntroCard';
import { TourneyCostData } from '@/types/tournaments.types';
import { TournamentRedirectAction } from '@/types/types';

interface IntroProps {
	data: IntroData;
	slug?: string;
	isManagerDetails: IsManagerDetails;
}
interface IntroData {
	status: string;
	regOpenDate: string;
	regCloseDate: string;
	regOpenTime: string;
	regCloseTime: string;
	currentRegPrice: string;
	venueName: string;
	street: string;
	cityStateZip: string;
	startTimesPosted: boolean;
	costsData: TourneyCostData[];
	currencyAbbr: string;
	timezoneAbbr: string;
	tourneyId: string;
	isAdvertiseOnly: boolean;
}

export const IntroCardsSection = ({ data, slug, isManagerDetails }: IntroProps) => {
	const {
		status,
		regOpenDate,
		regCloseDate,
		regOpenTime,
		regCloseTime,
		currentRegPrice,
		venueName,
		street,
		cityStateZip,
		startTimesPosted,
		costsData,
		currencyAbbr,
		timezoneAbbr,
		tourneyId,
		isAdvertiseOnly
	} = data;

	const [startTimesModal, setStartTimesModal] = useState(false);
	const [costsModal, setCostsModal] = useState(false);

	const [registerURL, setRegisterURL] = useState<Url>('');

	const { session } = useSession();

	const { hasPermission } = useAuth();

	const isSuperAdmin = hasPermission(Permission.SUPER_ADMIN);

	useEffect(() => {
		if (session?.user && session?.user?.uuid) {
			const url = new URL(`${process.env.NEXT_PUBLIC_PBRACKETS_URL}/redirect_r.aspx`);
			url.searchParams.set('mf', 't');
			url.searchParams.set('mid', tourneyId as string);
			url.searchParams.set('todo', TournamentRedirectAction.REGISTER);
			url.searchParams.set('rurl', window.location.href);
			setRegisterURL(url);
		} else {
			const url = new URL(`${process.env.NEXT_PUBLIC_SSO_ENDPOINT}/`);
			url.searchParams.set('flow', 'SelfService');
			url.searchParams.set('continue', window.location.href);

			setRegisterURL(url);
		}
	}, []);

	const getNextCost = () => {
		const currentIndex = costsData.findIndex((cost) => !!cost.IsCurrent);
		return costsData[currentIndex + 1];
	};

	return (
		<>
			<div
				className={clsx(
					'my-6 grid grid-cols-1 gap-6 sm:grid-cols-2',
					!isAdvertiseOnly || status === 'reg-open' || !status ? 'lg:grid-cols-3' : 'lg:grid-cols-2'
				)}
			>
				{(!isAdvertiseOnly || (isAdvertiseOnly && status === 'reg-open' && regOpenDate && regCloseDate)) && (
					<IntroCard
						title={'When'}
						titleIcon={<FontAwesomeIcon icon={faCalendar} />}
						subtitle={
							!isAdvertiseOnly && (
								<>
									{startTimesPosted ? (
										<CustomLink href={`/tournaments/${slug}/start-times`}>
											<Button
												className="text-blue-700 underline hover:!text-blue-600"
												variant="link"
												prefixIcon={<FontAwesomeIcon icon={faClock} />}
											>
												Start Times
											</Button>
										</CustomLink>
									) : (
										<Button
											onClick={() => setStartTimesModal(true)}
											className="text-blue-700 underline hover:!text-blue-600"
											variant="link"
											prefixIcon={<FontAwesomeIcon icon={faClock} />}
										>
											Start Times
										</Button>
									)}
								</>
							)
						}
						content={
							<div className="flex w-full flex-row gap-2">
								{status === 'reg-full' ? (
									<>
										<div className="my-4 w-full text-center text-lg font-bold text-gray-900">Registration capacity is full</div>
									</>
								) : status === 'reg-open' || !status || moment().isBefore(regCloseDate) ? (
									<>
										<div className="w-1/2 text-gray-900">
											<div className="mb-4 font-bold">Registration opens</div>
											<div>{regOpenDate}</div>
											<div className="font-medium text-gray-500">{regOpenTime}</div>
										</div>
										<div className="w-1/2 text-gray-900">
											<div className="mb-4 font-bold">Registration ends</div>
											<div>{regCloseDate}</div>

											<div className="font-medium text-gray-500">{regCloseTime}</div>
										</div>
									</>
								) : (
									<div className="my-4 w-full text-center text-lg font-bold text-gray-900">Registration is now closed</div>
								)}
							</div>
						}
					/>
				)}
				{(!isAdvertiseOnly || status === 'reg-open' || moment().isBefore(regCloseDate) || !status) && (
					<IntroCard
						title={status === 'reg-open' || moment().isBefore(regCloseDate) || !status ? 'Registration Cost' : 'In Queue & On Court'}
						titleIcon={status === 'reg-open' || moment().isBefore(regCloseDate) ? <FontAwesomeIcon icon={faCreditCard} /> : ''}
						content={
							status === 'reg-open' || moment().isBefore(regCloseDate) || !status ? (
								<>
									<div className="flex w-full flex-col items-center gap-2 text-gray-900">
										<div className={clsx('text-2xl font-medium text-blue-600', costsData.length < 2 && 'mt-4')}>
											{currentRegPrice}
										</div>
										{costsData.length >= 2 && (
											<>
												{!costsData[costsData.length - 1]?.IsCurrent && (
													<div className="italic">
														Price changes to {currencyAbbr} {getNextCost()?.Cost_Registration} on{' '}
														{moment.parseZone(getNextCost()?.DateStart).format('MMM Do')}
													</div>
												)}
												<div onClick={() => setCostsModal(true)} className="cursor-pointer text-sm text-blue-600 underline">
													Click here for fee schedule
												</div>
											</>
										)}
									</div>
								</>
							) : (
								<div className="flex w-full flex-col justify-center gap-2">
									<div className="flex w-full flex-col gap-2 md:flex-row">
										<CustomLink className="w-full" href={`/tournaments/${slug}/in-queue`}>
											<Button className="w-full justify-center bg-blue-600" variant="blue">
												Players In Queue
											</Button>
										</CustomLink>

										<CustomLink className="w-full" href={`/tournaments/${slug}/on-court`}>
											<Button className="w-full justify-center bg-blue-600" variant="blue">
												Players On Court
											</Button>
										</CustomLink>
									</div>
									{(!!isSuperAdmin || !!isManagerDetails.IsManager) && (
										<div className="grid grid-cols-2 gap-2">
											<CustomLink className="w-full" href={`/tournaments/${slug}/in-queue/kiosk`}>
												<Button className="w-full justify-center bg-blue-600" variant="blue">
													In Queue Kiosk Page
												</Button>
											</CustomLink>
											<CustomLink className="w-full" href={`/tournaments/${slug}/on-court/kiosk`}>
												<Button className="w-full justify-center bg-blue-600" variant="blue">
													On Court Kiosk Page
												</Button>
											</CustomLink>
											<CustomLink className="col-span-2 w-full" href={`/tournaments/${slug}/on-court/kiosk?rotate=true`}>
												<Button className="w-full justify-center bg-blue-600" variant="blue">
													In Queue/On Court Kiosk Page
												</Button>
											</CustomLink>
										</div>
									)}
								</div>
							)
						}
					/>
				)}

				<IntroCard
					title={'Where'}
					titleIcon={<FontAwesomeIcon icon={faLocationDot} />}
					subtitle={
						<>
							<a
								target="_blank"
								href={`https://www.google.com/maps?q=${venueName}, ${street}, ${cityStateZip}`}
								aria-label="Link to get directions (opens in a new tab)"
							>
								<div className="font-medium text-gray-600 underline">Get Directions</div>
							</a>
						</>
					}
					content={
						<>
							<div className="flex w-full flex-col gap-2 text-gray-900">
								<div className="font-bold">{venueName}</div>
								<div>{street}</div>
								<div className="font-medium text-gray-900">{cityStateZip}</div>
							</div>
						</>
					}
				/>
			</div>
			<Modal onOpenChange={() => setStartTimesModal(!startTimesModal)} open={startTimesModal} backdropClose={false}>
				<ModalContent
					className="flex max-w-[90vw] flex-col justify-center"
					style={{
						width: 480
					}}
				>
					<ModalHeading className="flex w-full items-center justify-between">
						<div className="flex-1 text-xl font-semibold capitalize leading-7 text-gray-900">Please check back</div>
						<ModalClose className="ml-auto">
							<FontAwesomeIcon icon={faTimes} size="xl" className="text-gray-500" />
						</ModalClose>
					</ModalHeading>
					<ModalDescription className="mt-4 text-base font-normal text-gray-600">
						Start times will be posted once the tournament registration has closed and brackets have been made.
					</ModalDescription>
				</ModalContent>
			</Modal>

			<Modal onOpenChange={() => setCostsModal(!costsModal)} open={costsModal} backdropClose={false}>
				<ModalContent className="flex w-[480px] max-w-[90vw] flex-col justify-center">
					<ModalHeading className="flex w-full items-center justify-between">
						<div className="flex-1 text-xl font-semibold capitalize leading-7 text-gray-900">
							<FontAwesomeIcon icon={faCreditCard} className="mr-2" />
							Fee Schedule
						</div>
						<ModalClose className="ml-auto">
							<FontAwesomeIcon icon={faTimes} size="xl" className="text-gray-500" />
						</ModalClose>
					</ModalHeading>
					<ModalDescription className="mt-4 max-h-[60vh] overflow-scroll text-base font-normal text-gray-600">
						{!costsData[costsData.length - 1]?.IsCurrent && (
							<div className="mb-2">
								Register soon as prices are set to increase on{' '}
								{moment.parseZone(getNextCost()?.DateStart).format('MMM DD, YYYY h:mm A')} {timezoneAbbr}
							</div>
						)}

						<div className="flex flex-col gap-2">
							{costsData.map((cost, i) => (
								<div key={i} className="flex flex-col gap-2 rounded-lg border p-4 sm:flex-row sm:items-center">
									<div className="sm:w-3/4">
										<div className="text-gray-500">
											Effective from: <br />
											{moment.parseZone(cost.DateStart).format('MMM DD, YYYY h:mm A')} {timezoneAbbr}
										</div>
										<div className="font-medium text-gray-700">
											<div>
												Registration Cost: {currencyAbbr} {cost.Cost_Registration}
											</div>
											{Number(cost.Cost_Registration_ClubMember) > 0 && (
												<div>
													Club Members Cost: {currencyAbbr} {cost.Cost_Registration_ClubMember}
												</div>
											)}
											{Number(cost.Cost_Registration_CollegiateDiscount) > 0 && (
												<div>
													Collegiate Discount Cost: {currencyAbbr} {cost.Cost_Registration_CollegiateDiscount}
												</div>
											)}

											{Number(cost.Cost_Registration_JuniorDiscount) > 0 && (
												<div>
													Junior Discount Cost: {currencyAbbr} {cost.Cost_Registration_JuniorDiscount}
												</div>
											)}
											{Number(cost.Cost_Registration_SeniorDiscount) > 0 && (
												<div>
													Senior Discount Cost: {currencyAbbr} {cost.Cost_Registration_SeniorDiscount}
												</div>
											)}

											{Number(cost.Cost_Registration_Lottery) > 0 && (
												<div>
													Lottery Cost: {currencyAbbr} {cost.Cost_Registration_Lottery}
												</div>
											)}
											{Number(cost.Cost_Registration_OutOfState) > 0 && (
												<div>
													Out of State Cost: {currencyAbbr} {cost.Cost_Registration_OutOfState}
												</div>
											)}
										</div>
									</div>
									{cost.IsCurrent && (
										<a className="contents" href={registerURL.toString()}>
											<Button
												prefixIcon={isManagerDetails.IsRegistered ? <FontAwesomeIcon icon={faEdit} /> : null}
												className="size-min justify-center bg-blue-600"
												variant="blue"
											>
												{isManagerDetails.IsRegistered ? 'Edit Reg.' : status === 'reg-open' ? 'Register' : null}
											</Button>
										</a>
									)}
								</div>
							))}
						</div>
					</ModalDescription>
				</ModalContent>
			</Modal>
		</>
	);
};
